import { useState } from 'react'
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { Link, useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import logo from '../../logo-black-no-bg.svg'

import { AuthToken } from '@/MODELS/types'

import { api_no_auth, setAccessToken } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

interface LoginResponse {
    token?: AuthToken
    redirectUrl?: string
    requiresOtp?: boolean
    otpSessionId?: string
}

// Where to send someone who tried to sign in here with a family account. The
// backend supplies appUrl on the rejection; this is only the fallback.
function familyAppFallbackUrl(): string {
    const origin = window.location.origin
    if (origin.includes('provider.localhost')) return origin.replace('provider.localhost', 'localhost')
    return origin.replace('//provider.', '//app.')
}

export default function ProviderLogin() {
    const navigate = useNavigate()
    const { fetchUser } = useAuth()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [familyAppUrl, setFamilyAppUrl] = useState<string | null>(null)

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (submitting) return
        setError(null)
        setFamilyAppUrl(null)
        setSubmitting(true)
        try {
            const response = await api_no_auth<LoginResponse>(
                ROUTES.auth.providerLogin,
                { email, password, portal: 'provider' },
                undefined,
                undefined,
                true
            )

            if (response.error) {
                const errData = response.data as { code?: string; appUrl?: string } | undefined
                setError(response.error)
                if (errData?.code === 'FAMILY_ACCOUNT') {
                    setFamilyAppUrl(errData.appUrl || familyAppFallbackUrl())
                }
                return
            }

            const data = response.data
            if (data?.requiresOtp) {
                // SMS verification only exists for platform admins; the portal
                // doesn't implement the OTP exchange.
                setError('This account requires SMS verification, which is only available through the family app sign-in.')
                return
            }
            if (!data?.token) {
                setError('Login unsuccessful. Please check your details and try again.')
                return
            }

            setAccessToken(data.token)
            await fetchUser()
            navigate(data.redirectUrl ?? '/dashboard', { replace: true })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className='flex flex-col items-center justify-center flex-1 min-h-screen px-4 py-12 bg-gradient-to-br from-brand-50 to-white'>
            <div className='w-full max-w-md'>
                <div className='flex flex-col items-center mb-8'>
                    <img src={logo} alt='BrightPath' className='h-16 mb-4' />
                    <h1 className='text-2xl font-bold text-neutral-900'>BrightPath for Providers</h1>
                    <p className='text-sm text-neutral-600 mt-1'>Sign in to your provider account</p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className='bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 space-y-4'
                >
                    <div>
                        <label className='block text-sm font-medium text-neutral-700 mb-1.5'>
                            Work email
                        </label>
                        <div className='relative'>
                            <EnvelopeIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400' />
                            <input
                                type='email'
                                autoComplete='email'
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className='w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
                            />
                        </div>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Password</label>
                        <div className='relative'>
                            <LockClosedIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400' />
                            <input
                                type='password'
                                autoComplete='current-password'
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className='w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
                            />
                        </div>
                    </div>

                    {error && (
                        <div className='p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg'>
                            {error}
                            {familyAppUrl && (
                                <div className='mt-1'>
                                    <a href={`${familyAppUrl}/login`} className='font-medium underline'>
                                        Go to the family app
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type='submit'
                        disabled={submitting}
                        className='w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        {submitting ? 'Signing in...' : 'Sign in'}
                    </button>

                    <div className='text-center'>
                        <Link to='/request-reset' className='text-sm text-brand-600 hover:underline'>
                            Forgot password?
                        </Link>
                    </div>
                </form>

            </div>
        </div>
    )
}
